// Landing guard for scrolls issued against a layout that is still SETTLING.
//
// Born for the two-phase PDF citation jump (SCAL-8986 follow-up): phase 2 centers
// the highlight with scrollIntoView, but on LONG documents the LAZY pages around
// the target keep mounting AFTERWARDS, growing from their placeholder height and
// shifting the document under a scroll position that was measured against the old
// layout. Observed with real data: on a 21-page document the first eyeball click
// parked ~2 pages short of the target and only a second click (by which time the
// layout had settled) landed. The review pane's card scroll (SCAL-9182) hits the
// same failure class: a card activated from the grid can mount inside the drawer's
// expand transition, where a one-shot scroll measures a pre-transition layout and
// parks a deep card off-view.
//
// This helper owns the landing: it issues the initial smooth scroll, then watches
// the target on a requestAnimationFrame loop - whenever its position has SETTLED
// (same top across two consecutive frames, i.e. no smooth-scroll animation and no
// layout shift in flight) but it is OUTSIDE its scroll viewport, the scroll is
// re-issued against the now-current layout. Both the watch window and the number
// of re-issued scrolls are bounded, so the loop can never run away; the returned
// cancel function makes it effect-cleanup safe.

export interface ScrollSettleOptions {
  /** How the scrolls align the target (default 'center'). */
  block?: ScrollLogicalPosition;
  /**
   * Total rAF frames to keep watching (~16ms each; 300 ~= 5s) - covers the
   * smooth-scroll animation plus the layout settling it rides on.
   */
  maxFrames?: number;
  /** Max scrolls re-issued after the initial one. */
  maxRescrolls?: number;
  /**
   * closest() selector for the scroll viewport the in-view check measures
   * against (default the PDF viewer's scroll container); the element's window
   * when no ancestor matches.
   */
  containerSelector?: string;
}

/**
 * True when the element has LANDED in its scroll viewport for the given block
 * alignment: block 'center' positions the vertical CENTER, so that is what must
 * lie inside; any other alignment ('nearest' scrolls the minimum to fully
 * reveal) is only satisfied by FULL visibility.
 */
const isInView = (el: Element, block: ScrollLogicalPosition, containerSelector: string): boolean => {
  const rect = el.getBoundingClientRect();
  const container = el.closest(containerSelector)?.getBoundingClientRect();
  const win = el.ownerDocument.defaultView;
  const top = container?.top ?? 0;
  const bottom = container?.bottom ?? win?.innerHeight ?? el.ownerDocument.documentElement.clientHeight;
  if (block === 'center') {
    const centerY = (rect.top + rect.bottom) / 2;
    return centerY >= top && centerY <= bottom;
  }
  return rect.top >= top && rect.bottom <= bottom;
};

/**
 * Smooth-scroll the element with the given id into view and keep re-landing it
 * until the layout settles with the element in view (see module comment). The
 * element is re-resolved by id every frame, so a page re-render replacing the
 * highlight node never strands the loop on a detached element. Returns a cancel
 * function for effect cleanup.
 */
export const scrollIntoViewUntilSettled = (
  doc: Document,
  elementId: string,
  {
    block = 'center',
    maxFrames = 300,
    maxRescrolls = 5,
    containerSelector = '#pdf-viewer-scroll-container',
  }: ScrollSettleOptions = {}
): (() => void) => {
  const win = doc.defaultView ?? window;
  let frames = 0;
  let rescrolls = 0;
  let rafId = 0;
  let lastTop: number | null = null;
  let cancelled = false;

  // Optional call: jsdom has no Element.scrollIntoView.
  doc.getElementById(elementId)?.scrollIntoView?.({ behavior: 'smooth', block });

  const step = () => {
    if (cancelled) {
      return;
    }
    frames += 1;
    const el = doc.getElementById(elementId);
    if (el) {
      const { top } = el.getBoundingClientRect();
      const settled = lastTop !== null && Math.abs(top - lastTop) < 0.5;
      lastTop = top;
      if (settled && !isInView(el, block, containerSelector)) {
        if (rescrolls >= maxRescrolls) {
          // Stable layout, still off-view, out of budget: nothing left to try.
          return;
        }
        rescrolls += 1;
        el.scrollIntoView?.({ behavior: 'smooth', block });
        // The re-issued scroll starts a new animation: restart settle detection.
        lastTop = null;
      }
      // Landed (settled + in view): keep IDLING for the rest of the frame
      // budget instead of stopping - the late lazy-page mounts this guard
      // exists for can shift the layout AFTER a quiet landing, and the idle
      // watch (one rect read per frame) re-lands those too.
    }
    if (frames < maxFrames) {
      rafId = win.requestAnimationFrame(step);
    }
  };

  rafId = win.requestAnimationFrame(step);

  return () => {
    cancelled = true;
    win.cancelAnimationFrame(rafId);
  };
};

export default scrollIntoViewUntilSettled;
